import type { ReactNode } from 'react'
import { LogIn, LogOut, Minus, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  type OccupancyState, type RoomCondition, type RoomVisualTone,
  readiness, roomTone,
} from '@hk/core'

/* ============================================================================
   RoomTile — the most important component in the product.

   A supervisor reads five hundred of these at seven in the morning and decides
   the day from what they see. Everything about it is therefore deliberate:

     HUE            housekeeping state, overridden by service availability
     CORNER CHIP    occupancy
     BORDER STYLE   solid = in service, dashed = out of service or order
     LABEL          the housekeeping state in words

   THREE CHANNELS, THREE DIMENSIONS. Do not add a fourth thing to the hue. If a
   new fact must appear on the tile, it needs its own channel or it does not
   belong here.

   Colour is never the only carrier: the state is also written on the tile, so
   a user with a colour vision deficiency loses nothing. That is the reason the
   label exists and it is why it must not be removed to save space.

   BRAND BLUE APPEARS NOWHERE IN THIS COMPONENT. Blue is reserved for chrome
   and action, so on a board of five hundred tiles anything blue is something
   you can click.
   ========================================================================= */

const toneClass: Record<RoomVisualTone, string> = {
  dirty: 'border-solid border-[var(--hk-hk-dirty-border)] bg-[var(--hk-hk-dirty-bg)]',
  prog:  'border-solid border-[var(--hk-hk-prog-border)]  bg-[var(--hk-hk-prog-bg)]',
  clean: 'border-solid border-[var(--hk-hk-clean-border)] bg-[var(--hk-hk-clean-bg)]',
  ready: 'border-solid border-[var(--hk-hk-ready-border)] bg-[var(--hk-hk-ready-bg)]',
  oos:   'border-dashed border-[var(--hk-sv-oos-border)]  bg-[var(--hk-sv-oos-bg)]',
  ooo:   'border-dashed border-[var(--hk-sv-ooo-border)]  bg-[var(--hk-sv-ooo-bg)]',
}

const toneText: Record<RoomVisualTone, string> = {
  dirty: 'text-[var(--hk-hk-dirty-text)]', prog: 'text-[var(--hk-hk-prog-text)]',
  clean: 'text-[var(--hk-hk-clean-text)]', ready: 'text-[var(--hk-hk-ready-text)]',
  oos:   'text-[var(--hk-sv-oos-text)]',   ooo:  'text-[var(--hk-sv-ooo-text)]',
}

const toneAccent: Record<RoomVisualTone, string> = {
  dirty: 'bg-[var(--hk-hk-dirty-accent)]', prog: 'bg-[var(--hk-hk-prog-accent)]',
  clean: 'bg-[var(--hk-hk-clean-accent)]', ready: 'bg-[var(--hk-hk-ready-accent)]',
  oos:   'bg-[var(--hk-sv-oos-accent)]',   ooo:  'bg-[var(--hk-sv-ooo-accent)]',
}

const occClass: Record<OccupancyState, string> = {
  OCCUPIED:  'bg-[var(--hk-occ-in-bg)]  text-[var(--hk-occ-in-fg)]',
  VACANT:    'bg-[var(--hk-occ-out-bg)] text-[var(--hk-occ-out-fg)]',
  ARRIVING:  'bg-[var(--hk-occ-arr-bg)] text-[var(--hk-occ-arr-fg)]',
  DEPARTING: 'bg-[var(--hk-occ-dep-bg)] text-[var(--hk-occ-dep-fg)]',
}

const occIcon: Record<OccupancyState, ReactNode> = {
  OCCUPIED:  <User className="size-2.5" />,
  VACANT:    <Minus className="size-2.5" />,
  ARRIVING:  <LogIn className="size-2.5" />,
  DEPARTING: <LogOut className="size-2.5" />,
}

/** Default English labels. A real application passes translated strings; these
 *  exist so the component is usable without wiring i18n first. */
const DEFAULT_LABELS: Record<RoomVisualTone, string> = {
  dirty: 'Dirty', prog: 'In progress', clean: 'Clean',
  ready: 'Inspected', oos: 'Out of service', ooo: 'Out of order',
}

const OCC_WORD: Record<OccupancyState, string> = {
  OCCUPIED: 'occupied', VACANT: 'vacant', ARRIVING: 'arriving today', DEPARTING: 'departing today',
}

export interface RoomTileProps {
  /** Identifier. Western digits in both languages — it is painted on the door. */
  number: string
  roomType?: string
  condition: RoomCondition
  selected?: boolean
  onSelect?: (number: string) => void
  /** Translated state labels. Falls back to English. */
  labels?: Partial<Record<RoomVisualTone, string>>
  className?: string
}

export function RoomTile({
  number, roomType, condition, selected, onSelect, labels, className,
}: RoomTileProps) {
  const tone = roomTone(condition)
  const { ready } = readiness(condition)
  const label = labels?.[tone] ?? DEFAULT_LABELS[tone]

  /* The screen-reader sentence carries all three dimensions plus the derived
     readiness, because a sighted user gets them from three visual channels at
     once and a screen-reader user must not have to hunt for them. */
  const description =
    `Room ${number}${roomType ? `, ${roomType}` : ''}. ${label}. ` +
    `${OCC_WORD[condition.occupancy]}. ${ready ? 'Ready to sell.' : 'Not ready.'}`

  return (
    <button
      type="button"
      onClick={onSelect ? () => onSelect(number) : undefined}
      aria-pressed={selected}
      aria-label={description}
      className={cn(
        'relative flex h-[70px] flex-col justify-between overflow-visible rounded-lg border-2',
        'px-[7px] pb-1.5 pt-1.5 text-start transition-all cursor-pointer',
        'hover:-translate-y-px hover:shadow-[0_6px_14px_-6px_rgb(30_58_95/0.35)]',
        'focus-visible:ring-3 focus-visible:ring-[color-mix(in_oklab,var(--hk-ring)_40%,transparent)]',
        toneClass[tone],
        selected && 'ring-3 ring-[rgb(56_189_248/0.35)]',
        className,
      )}
    >
      <span aria-hidden className={cn('absolute inset-x-1.5 top-0 h-[3px] rounded-b-full', toneAccent[tone])} />

      {/* leading-none clips: `truncate` sets overflow:hidden and a 1.0 line box
          does not contain a descender. Latin needs ~1.3 at 8.5px; ARABIC NEEDS
          MORE, because Naskh carries marks above and below the baseline that a
          Latin-tuned line box cuts off. 1.55 holds both. Do not tighten it to
          save a pixel — the first thing lost is the Arabic. */}
      <span className={cn('truncate text-[8.5px] font-semibold leading-[1.55] tracking-[0.02em]', toneText[tone])}>
        {label}
      </span>

      <span className="block">
        <span className={cn(
          'hk-ltr-run block text-[15px] font-bold leading-none tnum',
          tone === 'oos' || tone === 'ooo' ? 'text-muted-fg' : 'text-[#0f172a] dark:text-[#e8f4f8]',
        )}>
          {number}
        </span>
        {roomType && (
          <span className="mt-[3px] block truncate text-[8.5px] leading-none text-[#94a3b8]">{roomType}</span>
        )}
      </span>

      <span className="flex items-end justify-between gap-1">
        <span aria-hidden className="text-[8.5px] leading-none text-[#94a3b8]">
          {ready ? '✓' : ''}
        </span>
        <span
          aria-hidden
          className={cn('flex size-4 shrink-0 items-center justify-center rounded', occClass[condition.occupancy])}
        >
          {occIcon[condition.occupancy]}
        </span>
      </span>
    </button>
  )
}

/* ---- the legend that must accompany every board -------------------------
   A three-channel encoding is only readable if the key is present. The board
   is not complete without this. */

/* Every string here is a PROP with an English default, like every other string in this
   library. The two below were hardcoded and shipped that way: the Arabic room board
   rendered forty tiles, six translated tone labels, and then "corner chip = occupancy"
   and "dashed = not sellable" in English underneath them.

   That is the fourth time this exact defect has been found in this library, and every
   time it has been found by rendering the Arabic screen and looking at it — never by a
   typecheck, and never by a test. The rule it keeps proving: in a bilingual product a
   literal string inside a component is a defect, however small it looks. */
export function RoomLegend(
  { labels, occupancyNote = 'corner chip = occupancy', notSellableNote = 'dashed = not sellable' }:
  {
    labels?: Partial<Record<RoomVisualTone, string>>
    occupancyNote?: string
    notSellableNote?: string
  },
) {
  const tones: RoomVisualTone[] = ['dirty', 'prog', 'clean', 'ready', 'oos', 'ooo']
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-muted-fg">
      {tones.map((t) => (
        <span key={t} className="flex items-center gap-1.5">
          <span
            aria-hidden
            className={cn('size-3 shrink-0 rounded-[3px] border-2', toneClass[t])}
          />
          {labels?.[t] ?? DEFAULT_LABELS[t]}
        </span>
      ))}
      <span className="flex items-center gap-1.5">
        <span aria-hidden className={cn('flex size-4 items-center justify-center rounded', occClass.OCCUPIED)}>
          <User className="size-2.5" />
        </span>
        {occupancyNote}
      </span>
      <span className="flex items-center gap-1.5">
        <span aria-hidden className="size-3 shrink-0 rounded-[3px] border-2 border-dashed border-muted-fg" />
        {notSellableNote}
      </span>
    </div>
  )
}
